<template>
  <div
    class="sf-dropdown2-item"
    :class="dropdownItemClasses"
    data-testid="dropdown-item"
    :data-submenu-trigger="submenu"
    :aria-haspopup="submenu ? true : undefined"
    :aria-expanded="submenu ? activeSubmenu === submenu : undefined"
    v-bind="$attrs"
    @click="handleClick"
  >
    <component
      :is="effectiveHref ? 'a' : 'span'"
      :href="effectiveHref"
      :rel="effectiveHref ? 'noopener' : undefined"
      class="tw-inline-flex tw-flex-1"
      :class="contentClasses"
      data-testid="dropdown-item-content"
      @click="handleContentClick"
    >
      <slot name="icon" />
      <slot name="default" />
    </component>
    <!-- self-center: the item is a stretch-aligned flex row, so on multi-line
         items the glyph would otherwise ride along the top edge. -->
    <span
      v-if="hasCheckmark"
      class="sf-pad-l-1 fa fa-check tw-self-center"
    ></span>
    <span
      v-if="submenu"
      class="sf-pad-l-1 fa fa-chevron-right tw-self-center"
    ></span>
  </div>
</template>
<script setup lang="ts">
import { computed, inject, ref } from 'vue'

import { ACTIVE_SUBMENU } from '@app/shared/vue/composables/use-dropdown-submenu'

import { isNewTabClick } from '@app/utils/link-clicks'

defineOptions({
  name: 'SfDropdownItem',
})

interface DropdownProps {
  hasCheckmark?: boolean
  hasBorder?: boolean
  isDisabled?: boolean
  isSelected?: boolean
  isRounded?: boolean
  isShort?: boolean
  isTall?: boolean
  isWide?: boolean
  isWrapped?: boolean
  href?: string
  // Marks the item as the trigger row of the dropdown's submenu-<id> panel:
  // renders the chevron and the data-submenu-trigger attribute the dropdown's
  // submenu handling keys on.
  submenu?: string
}

const props = withDefaults(defineProps<DropdownProps>(), {
  href: undefined,
  isWrapped: true,
  hasBorder: true,
  submenu: undefined,
})

const emit =
  defineEmits<{
    click: [e: Event]
  }>()

// Open state of the submenu this row triggers, provided by SfDropdown.
const activeSubmenu = inject(ACTIVE_SUBMENU, ref(null))

const dropdownItemClasses = computed(() => {
  return {
    'sf-dropdown2-item--tall': props.isTall,
    'sf-disabled tw-pointer-events-auto tw-cursor-default': props.isDisabled,
    'sf-pointer': !props.isDisabled,
    'sf-nowrap': !props.isWrapped,
    'sf-dropdown2-item--no-border': !props.hasBorder,
    'sf-dropdown2-item--selected': props.isSelected,
    'tw-py-2 tw-px-4': props.isShort,
    'tw-py-4 tw-px-6': props.isWide && props.isTall,
    'tw-rounded': props.isRounded,
  }
})

// A disabled item must not offer a link the browser could still follow.
const effectiveHref = computed(() =>
  props.isDisabled ? undefined : props.href
)

// UIkit styles `.sf-dropdown2-item` for arbitrary elements and its bare `a`
// rules tie on specificity with the item's own hover colour, so neutralise the
// link styling rather than relying on stylesheet order.
const contentClasses = computed(() =>
  effectiveHref.value
    ? [
        'tw-text-inherit tw-no-underline',
        'hover:tw-text-inherit hover:tw-no-underline',
        'focus:tw-text-inherit focus:tw-no-underline',
        'active:tw-text-inherit active:tw-no-underline',
      ]
    : []
)

const handleContentClick = (event: MouseEvent) => {
  // Let the browser open its new tab: stop the event before the item root and
  // the dropdown root preventDefault it, but leave the default action intact.
  // Plain clicks fall through to `handleClick` on the root as before.
  if (effectiveHref.value && isNewTabClick(event)) {
    event.stopPropagation()
  }
}

const handleClick = (event: MouseEvent) => {
  // A modified click landing on the item's own padding, or on the checkmark,
  // has no anchor under the pointer, so there is nothing for the browser to
  // open. Bail out rather than navigating the current tab against the modifier.
  // Disabled items have no `effectiveHref` and so still fall through to the
  // unconditional preventDefault below, as they did before.
  if (effectiveHref.value && isNewTabClick(event)) return

  event.preventDefault()
  event.stopPropagation()

  if (props.isDisabled) {
    return
  }
  emit('click', event)

  if (effectiveHref.value) {
    window.Turbolinks.visit(effectiveHref.value)
  }
}
</script>
